import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createSupportTicket,
  listMySupportTickets,
} from "@/lib/support.functions";

const CATEGORY_LABELS: Record<string, string> = {
  integration_request: "Request an integration",
  question: "Ask a question",
  bug_report: "Report a bug",
  feedback: "Provide feedback",
  billing: "Billing question",
  other: "Other",
};

export function ContactSupportDialog({
  open,
  onOpenChange,
  defaultCategory,
  defaultSubject,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultCategory?: string;
  defaultSubject?: string;
}) {
  const qc = useQueryClient();
  const createFn = useServerFn(createSupportTicket);
  const [category, setCategory] = useState(defaultCategory ?? "question");
  const [subject, setSubject] = useState(defaultSubject ?? "");
  const [body, setBody] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      createFn({ data: { category: category as any, subject, body } }),
    onSuccess: () => {
      toast.success("Support ticket submitted. We'll be in touch via email.");
      qc.invalidateQueries({ queryKey: ["support-tickets-mine"] });
      setSubject("");
      setBody("");
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to submit"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Contact support</DialogTitle>
          <DialogDescription>
            Request an integration, report an issue, or share feedback. You'll see replies in{" "}
            <Link to="/settings/support" className="underline" onClick={() => onOpenChange(false)}>
              Settings → Support
            </Link>{" "}
            and via email.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Topic</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                category === "integration_request"
                  ? "e.g. Please add ConvertKit"
                  : "Brief summary"
              }
              maxLength={200}
            />
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={
                category === "integration_request"
                  ? "Which platform? What should we trigger when a customer's lifecycle changes?"
                  : "Tell us what's going on..."
              }
              rows={6}
              maxLength={5000}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={mutation.isPending || subject.trim().length < 3 || body.trim().length < 5}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Submitting..." : "Submit ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useMyTicketsCount() {
  const fetchFn = useServerFn(listMySupportTickets);
  return useQuery({
    queryKey: ["support-tickets-mine"],
    queryFn: () => fetchFn(),
  });
}

export const SUPPORT_CATEGORY_LABELS = CATEGORY_LABELS;

export const SUPPORT_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  waiting_on_user: "Waiting on you",
  resolved: "Resolved",
  closed: "Closed",
};
